import type { CSSProperties } from 'react';

import styled from 'styled-components';

export interface FlexContainerProps {
  justifyContent?:
    | 'unset'
    | 'normal'
    | 'start'
    | 'center'
    | 'end'
    | 'flex-start'
    | 'flex-end'
    | 'space-between'
    | 'space-around'
    | 'space-evenly'
    | 'stretch';
  alignItems?:
    | 'unset'
    | 'normal'
    | 'start'
    | 'center'
    | 'end'
    | 'flex-start'
    | 'flex-end'
    | 'self-start'
    | 'self-end'
    | 'baseline'
    | 'stretch';
  gap?: string;

  width?: string;
  height?: string;
  minWidth?: string;
  marginTop?: string;
  marginBottom?: string;
  flexWrap?: 'nowrap' | 'wrap' | 'wrap-reverse';

  position?: CSSProperties['position'];
  padding?: CSSProperties['padding'];
  flex?: CSSProperties['flex'];
}

/**
 * A styled div that displays its children in a flex layout.
 * @param {FlexContainerProps} props - The props for the component.
 * @returns {JSX.Element} - A React component.
 */
export const FlexContainer = styled.div<FlexContainerProps>`
  display: flex;
  flex-wrap: ${({ flexWrap }): string => flexWrap ?? 'unset'};
  justify-content: ${({ justifyContent }): string => justifyContent ?? 'unset'};
  align-items: ${({ alignItems }): string => alignItems ?? 'unset'};
  width: ${({ width }): string => width ?? 'unset'};
  height: ${({ height }): string => height ?? 'unset'};
  min-width: ${({ minWidth }): string => minWidth ?? 'unset'};

  margin-top: ${({ marginTop }): string => marginTop ?? 'unset'};
  margin-bottom: ${({ marginBottom }): string => marginBottom ?? 'unset'};

  gap: ${({ gap }): string => gap ?? 'unset'};

  position: ${({ position }): string => position ?? 'unset'};

  padding: ${({ padding }): CSSProperties['padding'] => padding ?? 'unset'};
  flex: ${({ flex }): CSSProperties['flex'] => flex ?? 'unset'};
`;

/**
 * A styled div that displays its children in a flex row layout.
 * @param {FlexContainerProps} props - The props for the component.
 * @returns {JSX.Element} - A React component.
 */
export const FlexRow = styled(FlexContainer)<FlexContainerProps>``;

/**
 * A styled div that displays its children in a flex column layout.
 * @param {FlexContainerProps} props - The props for the component.
 * @returns {JSX.Element} - A React component.
 */
export const FlexColumn = styled(FlexContainer)<FlexContainerProps>`
  flex-direction: column;
`;

/**
 * Utility styled component to center the children in its parent.
 */
export const CenterChildren = styled(FlexContainer)`
  justify-content: center;
  align-items: center;
  height: 100%;
`;
